import type { CandidateStore } from "../../../stores/automation/candidate.store";
import type { GroupLocator } from "../../../stores/automation/utils/action-group-walk";
import type { ActionCandidate } from "../../../types/candidate";
import { TRIGGER_NODE_ID } from "../graph/types";

export const actionsAtLocator = (
  candidate: CandidateStore,
  locator: GroupLocator,
): ActionCandidate[] => {
  switch (locator.kind) {
    case "root":
      return candidate.groupActions;
    case "loop":
      return candidate.looping.loopActionsAt(locator.instanceId);
    case "branch":
      return candidate.branches.branchActions({
        instanceId: locator.instanceId,
        branchHash: locator.branchHash,
      });
  }
};

const actionIndexIn = (actions: ActionCandidate[], hash: string): number =>
  actions.findIndex((action) => action.hash === hash);

/** Legacy Sidebar/StyledLine cascade: every action up to and including the
 *  active one is on the highlighted path (`activeActionIndex >= index`). */
export const actionCardHighlight = (
  activeActionHash: string | undefined,
  actionHash: string,
  actions: ActionCandidate[],
): { isHighlighted: boolean; isActive: boolean } => {
  const targetIndex = actionIndexIn(actions, actionHash);
  if (targetIndex < 0) {
    return { isHighlighted: false, isActive: false };
  }
  if (activeActionHash === undefined) {
    return { isHighlighted: false, isActive: false };
  }
  const activeIndex = actionIndexIn(actions, activeActionHash);
  if (activeIndex < 0) {
    return {
      isHighlighted: false,
      isActive: activeActionHash === actionHash,
    };
  }
  return {
    isHighlighted: activeIndex >= targetIndex,
    isActive: activeActionHash === actionHash,
  };
};

interface StepEdgeHighlight {
  isHighlighted: boolean;
  isActive: boolean;
}

/** Stroke colours for a step edge replica of StyledLine (left = cascade,
 *  bottom stub = active target). */
export const stepEdgeHighlight = (
  candidate: CandidateStore,
  activeActionHash: string | undefined,
  isTriggerActive: boolean,
  sourceId: string,
  targetId: string,
  targetLocator: GroupLocator | undefined,
  targetActionHash: string | undefined,
): StepEdgeHighlight => {
  if (targetActionHash !== undefined && targetLocator !== undefined) {
    const actions = actionsAtLocator(candidate, targetLocator);
    return actionCardHighlight(activeActionHash, targetActionHash, actions);
  }

  if (targetId === TRIGGER_NODE_ID || sourceId === TRIGGER_NODE_ID) {
    return {
      isHighlighted: isTriggerActive,
      isActive: isTriggerActive,
    };
  }

  return { isHighlighted: false, isActive: false };
};

export const parseActionHash = (nodeIdValue: string): string | undefined =>
  nodeIdValue.startsWith("action:")
    ? nodeIdValue.slice("action:".length)
    : undefined;
